import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import { contactLink } from '../topics';
import ContactForm from '../components/ContactForm';
import { useContact } from '../SiteContent';
import T from '../components/EditableText';
import PageHero from '../components/PageHero';

interface Job {
  /** Nummer der Stelle — bestimmt die Textschlüssel `karriere.stelle-N.…`. */
  number: number;
  locationIcon: string;
  image: string;
  alt: string;
  /** true = Bild rechts, Text links */
  mediaRight?: boolean;
}

const JOBS: Job[] = [
  {
    number: 1,
    locationIcon: 'map-pin',
    image: '/website/img/job-hauswirtschaft.jpg',
    alt: 'Mitarbeiterin bei der hauswirtschaftlichen Versorgung',
  },
  {
    number: 2,
    locationIcon: 'headset',
    image: '/website/img/job-kundenbetreuung.jpg',
    alt: 'Mitarbeiterin mit Headset in der Kundenbetreuung',
    mediaRight: true,
  },
  {
    number: 3,
    locationIcon: 'languages',
    image: '/website/img/job-koordination.jpg',
    alt: 'Team freut sich gemeinsam über einen Erfolg',
  },
];

const Karriere: React.FC = () => {
  const contact = useContact();

  usePageMeta(
    'Jobs in der Seniorenbetreuung in Gifhorn | Antczak',
    'Offene Stellen bei der Seniorenbetreuung Antczak in Gifhorn: Hauswirtschaft & Alltagsbegleitung, Kundenbetreuung und Koordination. Jetzt bewerben.',
    { standaloneTitle: true },
  );

  return (
    <>
      <PageHero
        eyebrowKey="karriere.hero.eyebrow"
        titleKey="karriere.hero.titel"
        accentKey="karriere.hero.titel-akzent"
        image={{ src: '/website/img/karriere-hero.jpg', alt: 'Das Team der Seniorenbetreuung Antczak', width: 900, height: 900 }}
      >
        <p className="hx-hero__lead">
          <T k="karriere.hero.absatz-1" />
        </p>
        <p className="hx-hero__lead">
          <T k="karriere.hero.absatz-2" />
        </p>
        <div className="btn-row hx-hero__actions">
          <a href="#stellen" className="btn btn--primary">
            <T k="karriere.hero.cta" />
          </a>
        </div>
      </PageHero>

      {/* Offene Stellen als Liste wie auf einem Stellenportal */}
      <section id="stellen" className="shell section" style={{ scrollMarginTop: 90 }}>
        <h2 className="h-section" style={{ marginBottom: 24 }}>
          <T k="karriere.stellen.titel" />
        </h2>
        <div className="hx-jobs">
          {JOBS.map((job) => (
            <article key={job.number} className="hx-job">
              <img src={job.image} alt={job.alt} loading="lazy" />
              <div className="hx-job__body">
                <span className="hx-note__mark">
                  <Icon name={job.locationIcon} size={15} />
                  <T k={`karriere.stelle-${job.number}.ort`} />
                </span>
                <h3 className="h-card">
                  <T k={`karriere.stelle-${job.number}.titel`} />
                </h3>
                <p>
                  <T k={`karriere.stelle-${job.number}.text`} />
                </p>
              </div>
              <Link to={contactLink('CAREER')} className="btn btn--ghost hx-job__cta">
                <T k={`karriere.stelle-${job.number}.cta`} /> <Icon name="arrow-right" size={16} />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* Initiativbewerbung */}
      <section id="bewerbung" className="section--tinted" style={{ scrollMarginTop: 90 }}>
        <div className="shell section hx-contact">
          <div className="hx-contact__info">
            <h2 className="h-section">
              <T k="karriere.initiativ.titel" />
            </h2>
            <p>
              <T k="karriere.initiativ.text" />
            </p>
            <a href={contact.emailHref} className="textlink">
              <Icon name="mail" size={18} />
              {contact.email}
            </a>
          </div>
          <div className="hx-contact__form">
            <ContactForm
              topic="CAREER"
              lockTopic
              submitLabelKey="formular.bewerbung-absenden"
              messagePlaceholder="Ein paar Worte zu dir"
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Karriere;
